import React from "react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatRupiah } from "@/lib/api";
import LikeButton from "@/components/LikeButton";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PER_PAGE = 20;

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(0);

  const type = params.get("type") || "";
  const category = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const p = {};
    if (type) p.type = type;
    if (category) p.category = category;
    if (params.get("q")) p.q = params.get("q");
    api.get("/products", { params: p }).then((r) => setProducts(r.data));
    setPage(0);
  }, [type, category, params]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
    setPage(0);
  };

  const totalPages = Math.ceil(products.length / PER_PAGE);
  const pageData = products.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="catalog-page">
      <div className="label-mono text-zinc-500">// CATALOG</div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight mt-1">Semua Produk</h1>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setParam("q", q); }} className="flex border border-black">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..."
            className="px-4 py-2 font-mono text-sm focus:outline-none" data-testid="search-input" />
          <button className="bg-black text-white px-4 font-mono text-xs uppercase font-bold tracking-widest" data-testid="search-btn">Cari</button>
        </form>

        <div className="flex border border-black">
          {[{ k: "", l: "Semua" }, { k: "digital", l: "Digital" }, { k: "physical", l: "Fisik" }].map((t) => (
            <button key={t.l} onClick={() => setParam("type", t.k)} data-testid={`filter-type-${t.k || "all"}`}
              className={`px-4 py-2 font-mono text-xs uppercase font-bold tracking-widest border-r border-black last:border-r-0 ${type === t.k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => setParam("category", "")} className={`badge-status ${!category ? "bg-black text-white" : "bg-white"}`} data-testid="cat-filter-all">SEMUA</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => setParam("category", c.id)} data-testid={`cat-filter-${c.slug}`}
            className={`badge-status ${category === c.id ? "bg-[#FF6A00] text-white" : "bg-white"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {/* PAGINATION TOP */}
      <div className="mt-6 flex items-center justify-between">
        <div className="font-mono text-xs text-zinc-500">
          {products.length} produk · halaman {page + 1} dari {totalPages || 1}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setPage(p => Math.max(0, p - 1)); window.scrollTo(0, 0); }}
            disabled={page === 0}
            className="flex items-center gap-1 px-3 py-1.5 border border-black font-mono text-xs font-bold hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft className="w-3 h-3" /> Sebelumnya
          </button>
          <div className="flex gap-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = totalPages <= 5 ? i : page < 3 ? i : page > totalPages - 3 ? totalPages - 5 + i : page - 2 + i;
              return (
                <button key={pageNum} onClick={() => { setPage(pageNum); window.scrollTo(0, 0); }}
                  className={`w-8 h-8 font-mono text-xs font-bold border ${page === pageNum ? "bg-black text-white border-black" : "border-black hover:bg-zinc-100"}`}>
                  {pageNum + 1}
                </button>
              );
            })}
          </div>
          <button onClick={() => { setPage(p => Math.min(totalPages - 1, p + 1)); window.scrollTo(0, 0); }}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-1 px-3 py-1.5 border border-black font-mono text-xs font-bold hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
            Berikutnya <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 border-l border-t border-black">
        {pageData.map((p) => (
          <Link key={p.id} to={`/product/${p.id}`} className="group border-r border-b border-black bg-white" data-testid={`product-card-${p.id}`}>
            <div className="aspect-square overflow-hidden border-b border-black bg-zinc-100 relative">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:grayscale transition-all" />
              <div className="absolute top-2 right-2">
                <LikeButton product={p} />
              </div>
              {p.sold_count > 0 && (
                <div className="absolute bottom-2 left-2 badge-status bg-black text-white">
                  🔥 {p.sold_count} TERJUAL
                </div>
              )}
            </div>
            <div className="p-4">
              <span className="badge-status bg-white">{p.type === "digital" ? "DIGITAL" : "FISIK"}</span>
              <h3 className="font-display text-base font-bold uppercase mt-2 line-clamp-2">{p.name}</h3>
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono text-sm font-bold">{formatRupiah(p.price)}</span>
                <span className="label-mono text-[#FF6A00]">BUY →</span>
              </div>
            </div>
          </Link>
        ))}
        {pageData.length === 0 && (
          <div className="col-span-full p-12 border-r border-b border-black text-center font-mono text-sm text-zinc-500">
            // Tidak ada produk
          </div>
        )}
      </div>

      {/* PAGINATION BOTTOM */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button onClick={() => { setPage(p => Math.max(0, p - 1)); window.scrollTo(0, 0); }}
            disabled={page === 0}
            className="flex items-center gap-1 px-3 py-1.5 border border-black font-mono text-xs font-bold hover:bg-black hover:text-white disabled:opacity-30">
            <ChevronLeft className="w-3 h-3" /> Sebelumnya
          </button>
          <span className="font-mono text-xs text-zinc-500">{page + 1} / {totalPages}</span>
          <button onClick={() => { setPage(p => Math.min(totalPages - 1, p + 1)); window.scrollTo(0, 0); }}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-1 px-3 py-1.5 border border-black font-mono text-xs font-bold hover:bg-black hover:text-white disabled:opacity-30">
            Berikutnya <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}